import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { jsPDF } from "jspdf";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../config/configApi";
import Swal from "sweetalert2";
import { BsPlus, BsTrash, BsArrowLeft, BsMic, BsMicFill, BsType, BsSave, BsFilePdf } from "react-icons/bs";
import Cookies from "js-cookie";

const RotaFicticia: React.FC = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioText, setAudioText] = useState("");
  const [error, setError] = useState("");
  const [isTypingMode, setIsTypingMode] = useState(true);
  const [signature, setSignature] = useState("");
  const [signatureProfissional, setSignatureProfissional] = useState("");
  const [img, setUploadedImages] = useState<string[]>([]);
  const [fileInputText, setFileInputText] = useState<string>("");

  const navigate = useNavigate();

  useEffect(() => {
    // Buscar o nome do usuário do cookie
    const username = Cookies.get("Username");
    
    if (username) {
      setSignatureProfissional(username);
    }
  }, []);

  const currentDate = new Date().toLocaleDateString("pt-BR");

  const SpeechRecognition =
    (window as any).SpeechRecognition ||
    (window as any).webkitSpeechRecognition;
  const recognition = SpeechRecognition ? new SpeechRecognition() : null;

  if (recognition) {
    recognition.lang = "pt-BR";
    recognition.interimResults = false;
    recognition.continuous = true;

    recognition.onresult = (event: any) => {
      const transcript = Array.from(event.results)
        .map((result: any) => result[0].transcript)
        .join(" ");
      const newText = (audioText + " " + transcript).slice(0, 5000);
      setAudioText(newText);
    };

    recognition.onerror = (event: any) => {
      setError(`Erro ao processar áudio: ${event.error}`);
      setIsRecording(false);
      setIsPaused(false);
    };
  }

  const startRecording = () => {
    if (!recognition) {
      setError("Reconhecimento de voz não suportado no navegador.");
      return;
    }
    setError("");
    setIsRecording(true);
    setIsPaused(false);
    recognition.start();
  };

  const pauseRecording = () => {
    if (recognition) {
      recognition.stop();
      setIsPaused(true);
      setIsRecording(false);
    }
  };

  const resetRecording = () => {
    if (recognition) {
      recognition.stop();
    }
    setIsRecording(false);
    setIsPaused(false);
    setAudioText("");
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const filesArray = Array.from(event.target.files);
      const newImages: string[] = [];

      if (img.length + filesArray.length <= 3) {
        filesArray.forEach((file) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            newImages.push(reader.result as string);
            if (newImages.length === filesArray.length) {
              setUploadedImages((prevImages) => [...prevImages, ...newImages]);
            }
          };
          reader.readAsDataURL(file);
        });
      } else {
        Swal.fire({
          icon: "warning",
          title: "Limite de fotos alcançado",
          text: "Você pode adicionar no máximo 3 fotos.",
          confirmButtonText: "Ok",
        });
      }
    }
  };

  const handleRemoveImage = (indexToRemove: number) => {
    setUploadedImages((prevImages) =>
      prevImages.filter((_, index) => index !== indexToRemove)
    );
  };

  useEffect(() => {
    if (img.length === 0) {
      setFileInputText("");
    } else if (img.length === 1) {
      setFileInputText("1 arquivo selecionado");
    } else {
      setFileInputText(`${img.length} arquivos selecionados`);
    }
  }, [img]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const saveText = async () => {
    try {
      const token = localStorage.getItem("Frontend");

      const response = await api.post(
        "/sadprontuarioatendimentoimagem/salvar",
        {
          nome: patientData.nome,
          nu_cpf: patientData.nu_cpf, 
          cd_paciente: patientData.cd_paciente,
          text: audioText,
          signature,
          img,
          date: currentDate,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 201) {
        await Swal.fire({
          icon: "success",
          title: "Prontuário salvo com sucesso!",
          showConfirmButton: false,
          timer: 1500,
        });

        const generatePDFResponse = await Swal.fire({
          title: "Deseja gerar o PDF do prontuário?",
          showCancelButton: true,
          confirmButtonText: "Sim",
          cancelButtonText: "Não",
        });

        if (generatePDFResponse.isConfirmed) {
          generatePDF();
        }

        await Swal.fire({
          icon: "info",
          title: "Prontuário salvo!",
          text: generatePDFResponse.isConfirmed
            ? "O PDF foi gerado com sucesso."
            : "O prontuário foi salvo, mas o PDF não foi gerado.",
        });

        setAudioText("");
        setSignature("");
        setUploadedImages([]);
      } else {
        await Swal.fire({
          icon: "error",
          title: "Erro ao salvar prontuário",
          text: "Houve um erro ao tentar salvar o prontuário. Tente novamente.",
        });
      }
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Erro ao conectar com o servidor",
        text: `Ocorreu um erro ao tentar se conectar com o servidor: ${err}`,
      });
    }
  };

  const generatePDF = () => {
    const doc = new jsPDF();

    const logoUrl =
      "https://i0.wp.com/afrafepsaude.com.br/wp-content/uploads/2021/06/logo.png?w=692&ssl=1";
    const logoWidth = 50;
    const logoHeight = 20;
    const logoX = (doc.internal.pageSize.width - logoWidth) / 2;
    const logoY = 10;

    doc.addImage(logoUrl, "PNG", logoX, logoY, logoWidth, logoHeight);

    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    const title = "Prontuário de Atendimento";
    const titleWidth = doc.getTextWidth(title);
    const titleX = (doc.internal.pageSize.width - titleWidth) / 2;
    doc.text(title, titleX, 43);

    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    const pacienteText = `Paciente: ${patientData.nome}`;
    const pacienteWidth = doc.getTextWidth(pacienteText);
    const pacienteX = (doc.internal.pageSize.width - pacienteWidth) / 2;
    doc.text(pacienteText, pacienteX, 63);

    doc.setFontSize(10);
    doc.text("", 20, 75);

    const marginLeft = 20;
    const marginTop = 85;
    const pageWidth = doc.internal.pageSize.width - 40;
    const text = audioText;

    doc.setFontSize(10);
    doc.text(doc.splitTextToSize(text, pageWidth), marginLeft, marginTop);

    doc.setFontSize(12);
    doc.setFont("helvetica", "italic");

    const time = new Date().toLocaleTimeString("pt-BR");
    const signatureDateText = `Data: ${currentDate} Hora: ${time}`;

    const marginLeft2 = 20;
    const signatureDateY = doc.internal.pageSize.height - 40;
    const signatureTextY = signatureDateY + 6;
    const professionalSignatureY = signatureTextY + 6;

    doc.text(signatureDateText, marginLeft2, signatureDateY);
    doc.text(`Assinatura do responsável: ${signature}`, marginLeft, signatureTextY);
    doc.text(`Assinatura do profissional: ${signatureProfissional}`, marginLeft, professionalSignatureY);

    doc.save(`prontuario_${patientData.nome}.pdf`);
  };

  const handleTypingModeToggle = () => {
    if (isTypingMode) {
      setAudioText("");
    }
    setIsTypingMode(!isTypingMode);
  };

  const location = useLocation();
  const { patientData } = location.state || {};
  
  if (!patientData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <h2 className="text-xl font-bold text-red-500 mb-4 text-center">
            Nenhum dado disponível.
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="w-full bg-red-500 text-white py-3 px-4 rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center"
          >
            <BsArrowLeft className="mr-2" />
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
        <motion.div
          className="flex justify-center mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <img
            src="https://i0.wp.com/afrafepsaude.com.br/wp-content/uploads/2021/06/logo.png?w=692&ssl=1"
            alt="Logo Afrafepsaúde"
            className="w-60 h-auto"
          />
        </motion.div>
        
        <motion.div
          className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
            <h1 className="text-2xl font-bold text-center">Prontuário de Atendimento</h1>
          </div>
          
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                <BsArrowLeft className="mr-2" />
                Voltar
              </button>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h2 className="text-xl font-semibold text-blue-800 mb-3">Dados do Paciente</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-blue-700"><strong>Nome:</strong> {patientData.nome}</p>
                </div>
                <div>
                  <p className="text-blue-700"><strong>Idade:</strong> {patientData.idade}</p>
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
                <p>{error}</p>
              </div>
            )}

            <div className="flex justify-between items-center mb-6">
              <button
                onClick={handleTypingModeToggle}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  isTypingMode 
                    ? "bg-teal-500 text-white hover:bg-teal-600" 
                    : "bg-indigo-500 text-white hover:bg-indigo-600"
                }`}
              >
                {isTypingMode ? (
                  <>
                    <BsMic className="mr-2" />
                    Modo de Gravação
                  </>
                ) : (
                  <>
                    <BsType className="mr-2" />
                    Modo de Digitação
                  </>
                )}
              </button>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações do Atendimento
              </label>
              <textarea
                value={audioText}
                onChange={(e) => setAudioText(e.target.value)}
                className="w-full h-48 p-4 text-lg border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Digite ou veja o texto do áudio aqui..."
                readOnly={!isTypingMode}
              />
            </div>

            {audioText.trim() && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assinatura do Responsável
                    </label>
                    <input
                      type="text"
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Digite sua assinatura aqui..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assinatura do Profissional
                    </label>
                    <p className="w-full p-3 border border-gray-300 rounded-lg bg-gray-100">
                      {signatureProfissional}
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Anexar Fotos (Máximo 3)
                  </label>
                  <div className="flex items-center gap-4">
                    {img.length === 0 && (
                      <label className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600 transition-colors">
                        <BsPlus className="mr-2" />
                        Escolher arquivos
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-2">{fileInputText}</p>

                  <div className="flex gap-4 flex-wrap mt-4">
                    {img.map((image, index) => (
                      <div
                        key={index}
                        className="relative w-24 h-24 overflow-hidden rounded-lg border border-gray-200 shadow-md"
                      >
                        <img
                          src={image}
                          alt={`Uploaded ${index}`}
                          className="object-cover w-full h-full"
                        />
                        <button
                          onClick={() => handleRemoveImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 transition"
                        >
                          <BsTrash size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {!isTypingMode && (
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                {isRecording ? (
                  <button
                    onClick={pauseRecording}
                    className="flex-1 flex items-center justify-center px-4 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                  >
                    <BsMicFill className="mr-2" />
                    Pausar Gravação
                  </button>
                ) : (
                  <button
                    onClick={startRecording}
                    className="flex-1 flex items-center justify-center px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <BsMic className="mr-2" />
                    {isPaused ? "Continuar Gravação" : "Iniciar Gravação"}
                  </button>
                )}
                <button
                  onClick={resetRecording}
                  className="flex-1 flex items-center justify-center px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  <BsTrash className="mr-2" />
                  Limpar Gravação
                </button>
              </div>
            )}

            {audioText.trim() && (
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={saveText}
                  className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <BsSave className="mr-2" />
                  Salvar Prontuário
                </button>
                <button
                  onClick={generatePDF}
                  className="flex-1 flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <BsFilePdf className="mr-2" />
                  Gerar PDF
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default RotaFicticia;